import { HttpEngine } from 'base/auth_service';
import {
  Preferences,
  preferencesSchema,
  UpdatePreferencesRequest,
} from 'base/preferences';
import { productRestrictionsSchema } from 'base/product_restrictions';
import { FeatureFlags, featureFlagsSchema } from 'blocks/blocks_service';
import { z } from 'zod';
import { imageAssetSchema } from './asset';

export interface AccountsService {
  me(): Promise<MeResponse>;
  getBalance(): Promise<BalanceResponse>;
  getReferralRewards(): Promise<ReferralRewardResponse>;
  getReferralInfo(code: string): Promise<ReferralInfoResponse>;
  getAnnouncement(id: string | number): Promise<Announcement>;
  login(req: LoginRequest): Promise<LoginResponse>;
  signup(req: SignupRequest): Promise<SignupResponse>;
  verifyEmail(req: VerifyEmailRequest): Promise<VerifyEmailResponse>;
  requestVerificationCode(
    req: RequestVerificationCodeRequest,
  ): Promise<RequestVerificationCodeResponse>;
  resetPassword(req: ResetPasswordRequest): Promise<ResetPasswordResponse>;
  changeEmailStart(req: ChangeEmailStartRequest): Promise<{}>;
  changeEmailFinish(req: ChangeEmailFinishRequest): Promise<{}>;
  getChurnkeyHash(): Promise<GetChurnKeyHashResponse>;
  linkOAuth(req: LinkOauthRequest): Promise<LinkOauthResponse>;
  getGrowthNudges(): Promise<GrowthNudgesResponse>;
  updateGrowthNudge(
    key: keyof GrowthNudgesResponse,
    req: UpdateGrowthNudgeRequest,
  ): Promise<unknown>;
  getPreferences(): Promise<Preferences>;
  updatePreference(
    key: keyof Preferences,
    req: UpdatePreferencesRequest,
  ): Promise<unknown>;
  updateAttributes(
    req: UpdateAttributesRequest,
  ): Promise<UpdateAttributesResponse>;
  uploadAvatar(file: File): Promise<UploadAssetResponse>;
  deleteAvatar(): Promise<{}>;
  uploadTeamAvatar(file: File): Promise<UploadAssetResponse>;
  deleteTeamAvatar(): Promise<{}>;
  uploadTeamLogo(file: File): Promise<UploadAssetResponse>;
  deleteTeamLogo(): Promise<{}>;
  getPricingTable(
    req?: GetPricingTableRequest,
  ): Promise<GetPricingTableResponse>;
  getCoupons(): Promise<GetCouponsResponse>;
  acceptPlanOffer(
    req: AcceptPlanOfferRequest,
  ): Promise<AcceptPlanOfferResponse>;
  saveChurnFeedback(req: SaveChurnFeedbackRequest): Promise<{}>;
  getPausePlanOffer(): Promise<GetPausePlanOfferResponse>;
  acceptPausePlanOffer(req: AcceptPausePlanOfferRequest): Promise<{}>;
  resumePlanNow(): Promise<{}>;
  getCancelCouponOffer(): Promise<GetCancelCouponOfferResponse>;
  acceptCancelCouponOffer(): Promise<{}>;
  cancelPlanNow(): Promise<CancelPlanNowResponse>;
  reactivateCancellingPlan(): Promise<ReactiveCancellingPlanResponse>;
  createStripeBillingSession(
    req: CreateStripeBillingSessionRequest,
  ): Promise<CreateStripeBillingSessionResponse>;
  getReferralLink(): Promise<GetReferralLinkResponse>;
  getTeam(): Promise<GetTeamResponse>;
  teamUpdate(req: TeamUpdateRequest): Promise<TeamUpdateResponse>;
  teamInvite(req: TeamInviteRequest): Promise<TeamInviteResponse>;
  teamChangeInvite(
    req: TeamChangeInviteRequest,
  ): Promise<TeamChangeInviteResponse>;
  teamDeleteInvite(
    req: TeamDeleteInviteRequest,
  ): Promise<TeamDeleteInviteResponse>;
  teamRemoveMember(
    req: TeamRemoveMemberRequest,
  ): Promise<TeamRemoveMemberResponse>;
  teamAcceptInvite(
    req: TeamAcceptInviteRequest,
  ): Promise<TeamAcceptInviteResponse>;
  getFeatureFlags(): Promise<FeatureFlags>;
  exchangeGoogleAccessTokenForToken(
    req: ExchangeGoogleAccessTokenRequest,
  ): Promise<ExchangeGoogleAccessTokenResponse>;
  teamGetInvitationLinkInfo(): Promise<TeamGetInvitationLinkInfoResponse>;
  teamGetOrCreateInvitationLink(): Promise<TeamGetOrCreateInvitationLinkResponse>;
  teamResetInvitationLink(): Promise<TeamResetInvitationLinkResponse>;
  teamDisableInvitationLink(): Promise<TeamDisableInvitationLinkResponse>;
}

export const PlanName = {
  FREE: 'client',
  STARTER: 'starter',
  PRO: 'creator',
  TEAM: 'team',
} as const;
const planNameSchema = z.nativeEnum(PlanName);
export type PlanNameEnum = z.infer<typeof planNameSchema>;

const announcementSchema = z.object({
  id: z.number(),
  pretitle: z.string(),
  title: z.string(),
  body: z.string(),
  learnMoreUrl: z.string(),
  imageUrl: z.string(),
});
export type Announcement = z.infer<typeof announcementSchema>;

const teamRoleSchema = z.enum(['admin', 'member']);
export type TeamRole = z.infer<typeof teamRoleSchema>;

const referralInfoSchema = z.object({
  discountPercentage: z.number(),
  referrerName: z.string(),
});
export type ReferralInfoResponse = z.infer<typeof referralInfoSchema>;

const AccountTypeEnum = z.enum(['standard', 'google']);
export type AccountType = z.infer<typeof AccountTypeEnum>;
export const meResponseSchema = z.object({
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  color: z.string().regex(/^#[0-9a-f]{6}$/),
  avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
  role: teamRoleSchema,
  accountType: AccountTypeEnum,
  team: z.object({
    id: z.number(),
    name: z.string(),
    color: z.string().regex(/^#[0-9a-f]{6}$/),
    avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
    planName: planNameSchema,
    billingPeriod: z.enum(['monthly', 'yearly', 'forever']),
  }),
  isActive: z.boolean(),
  countryCode: z.string(),
  parityP: z
    .object({
      couponCode: z.string().nullable(),
      discountPercent: z.number().nullable(),
      bannerCopy: z.string().nullable(),
    })
    .nullable(),
  activeReferralPromotion: z
    .object({
      expiredTime: z.number(),
      discountPercent: z.number(),
      referrerName: z.string(),
    })
    .nullable(),
  announcement: announcementSchema.nullish().transform((x) => x ?? undefined),
  hasPaymentProblem: z.boolean().default(false),
  trialDaysRemaining: z.number().nullish(),
  daysSubscribed: z.number().nullish(),
  planName: planNameSchema,
  billingPeriod: z.enum(['monthly', 'yearly', 'forever']),
  dateJoined: z.number(),
  legacyPlanName: planNameSchema.nullable(),
  member: z.object({
    id: z.number(),
    email: z.string(),
    attributes: z
      .object({
        name: z.string(),
        'last-name': z.string(),
        'what-best-describes-you': z.string(),
        'company-name': z.string(),
        'company-size': z.string(),
        'company-type': z.string(),
        'how-did-you-hear-about-us': z.string(),
        'reason-for-churn': z.string(),
        'churned-feedback': z.string(),
        'signup-form-location': z.string(),
        'ai-opt-in': z.boolean(),
      })
      .partial(),
    favourites: z.array(z.string()),
    // The backend will send an {} if there's no membership... Why use null when you can use empty object!
    membership: z
      .object({
        status: z.string(), // TODO(slater): make this exhaustive
        // anonymous | free | paid | pro | trialing
        current_period_end: z.number().nullable(),
        current_period_start: z.number().nullable(),
        plan: z
          .object({
            priceId: z.string(),
            interval: z.string(),
          })
          .partial(),
      })
      .partial(),
    member: z.object({
      email: z.string(),
      customFields: z.object({
        name: z.string(),
        'last-name': z.string(),
      }),
    }),
  }),
  productRestrictions: productRestrictionsSchema,
  sgbBeta: z.boolean(),
  sgbWaitlist: z.boolean(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

type LoginRequest = {
  email: string;
  password: string;
};
const loginResponseSchema = z.object({
  accessToken: z.string(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

const balanceResponseSchema = z.object({
  balance: z.number(),
  currency: z.string(),
});
export type BalanceResponse = z.infer<typeof balanceResponseSchema>;

const referralRewardSchema = z.object({
  referredAccountName: z.string(),
  rewardCredit: z.string(),
  converted: z.boolean(),
  createdAt: z.number(),
  avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
  avatarColor: z.string(),
});
const referralRewardResponseSchema = z.object({
  rewards: z.array(referralRewardSchema).default([]),
});
export type ReferralRewardResponse = z.infer<
  typeof referralRewardResponseSchema
>;

type SignupRequest = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  signupReferral?: string;
  isCommenter?: boolean;
  fbc?: string;
  fbp?: string;
  gclid?: string;
  referralCode?: string;
};
const signupResponseSchema = z.object({
  userId: z.number(),
});
export type SignupResponse = z.infer<typeof signupResponseSchema>;

type VerifyEmailRequest = {
  email: string;
  code: string;
};
const verifyEmailResponseSchema = z.object({
  accessToken: z.string(),
  resetPasswordEntitlement: z.string(),
});
export type VerifyEmailResponse = z.infer<typeof verifyEmailResponseSchema>;

type RequestVerificationCodeRequest = {
  email: string;
};
const requestVerificationCodeResponseSchema = z.object({});
export type RequestVerificationCodeResponse = z.infer<
  typeof requestVerificationCodeResponseSchema
>;

type ResetPasswordRequest = {
  newPassword: string;
} & (
  | {
      oldPassword: string;
      entitlement?: undefined;
    }
  | {
      oldPassword?: undefined;
      entitlement: string;
    }
);
const resetPasswordResponseSchema = z.object({});
export type ResetPasswordResponse = z.infer<typeof resetPasswordResponseSchema>;

type ChangeEmailStartRequest = {
  newEmail: string;
};

type ChangeEmailFinishRequest = {
  newEmail: string;
  code: string;
};

type LinkOauthRequest = {
  writeKey: string;
  token: string;
};

const getChurnkeyHashResponseSchema = z.object({
  hash: z.string(),
  customerId: z.string(),
  subscriptionId: z.string(),
});
type GetChurnKeyHashResponse = z.infer<typeof getChurnkeyHashResponseSchema>;

const linkOauthResponseSchema = z.object({});
export type LinkOauthResponse = z.infer<typeof linkOauthResponseSchema>;

type UpdateAttributesRequest = {
  firstName?: string;
  lastName?: string;
  'ai-opt-in'?: boolean;
};
const updateAttributesResponseSchema = z.object({});
export type UpdateAttributesResponse = z.infer<
  typeof updateAttributesResponseSchema
>;

// We keep `avatar` for backwards compatibility; we should use `asset` in the future:
const uploadAssetResponseSchema = z.union([
  z.object({ asset: imageAssetSchema }),
  z.object({ avatar: imageAssetSchema }),
]);
export type UploadAssetResponse = z.infer<typeof uploadAssetResponseSchema>;

const growthNudgeKeySchema = z.enum(['contactSales']);
export type GrowthNudgeKey = z.infer<typeof growthNudgeKeySchema>;
const growthNudgeStateSchema = z.enum(['show', 'clicked', 'dismissed']);
export type GrowthNudgeState = z.infer<typeof growthNudgeStateSchema>;
const growthNudgesResponseSchema = z.record(
  growthNudgeKeySchema,
  growthNudgeStateSchema.optional(),
);
export type GrowthNudgesResponse = z.infer<typeof growthNudgesResponseSchema>;
const updateGrowthNudgeRequestSchema = z.object({
  state: growthNudgeStateSchema,
});
export type UpdateGrowthNudgeRequest = z.infer<
  typeof updateGrowthNudgeRequestSchema
>;

type GetPricingTableRequest = {
  includeNextPayment?: boolean;
};
const planOfferSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('available'),
    centsPerMemberPerMonth: z.number(),
    originalCentsPerMemberPerMonth: z.number().nullish(),
    isCurrentPlan: z.boolean(),
    isOldPrice: z.boolean(),
    isUpgrade: z.boolean(),
    trialPeriodDays: z.number(),
  }),
  z.object({
    status: z.literal('unavailable'),
    reason: z.string(),
    centsPerMemberPerMonth: z.number(),
    originalCentsPerMemberPerMonth: z.number().nullish(),
  }),
]);
export type PlanOffer = z.infer<typeof planOfferSchema>;
export const paidPricingTableKeySchema = z.enum([
  'starterMonthly',
  'starterYearly',
  'proMonthly',
  'proYearly',
  'teamMonthly',
  'teamYearly',
]);
export type PaidPricingTableKey = z.infer<typeof paidPricingTableKeySchema>;
const pricingTableSchema = z.object({
  free: planOfferSchema,
  starterMonthly: planOfferSchema,
  starterYearly: planOfferSchema,
  proMonthly: planOfferSchema,
  proYearly: planOfferSchema,
  teamMonthly: planOfferSchema,
  teamYearly: planOfferSchema,
});
export type PricingTable = z.infer<typeof pricingTableSchema>;
export type PricingTableKey = keyof PricingTable;
const getPricingTableResponseSchema = z.object({
  table: pricingTableSchema,
  cancelsAt: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  resumption: z
    .object({
      resumeAt: z.string(),
      billingPeriod: z.enum(['monthly', 'yearly', 'forever']),
      priceCents: z.number(),
      priceCurrency: z.string(),
      comparisionPriceCents: z.number(),
    })
    .nullish()
    .transform((x) => x ?? undefined),
  nextPayment: z
    .object({
      date: z.string(),
      amountDueCents: z.number(),
      seats: z.number(),
    })
    .nullish()
    .transform((x) => x ?? undefined),
  activeCoupon: z
    .object({
      discountPercent: z.number(),
      couponCode: z.string(),
    })
    .nullish()
    .transform((x) => x ?? undefined),
  trialEndDate: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
  hasPaymentProblem: z.boolean(),
  canManageBilling: z.boolean(),
});
export type GetPricingTableResponse = z.infer<
  typeof getPricingTableResponseSchema
>;

const couponOfferStatusSchema = z.object({
  couponCode: z.string(),
  bannerUiName: z
    .string()
    .nullable()
    .transform((x) => x ?? undefined),
  daysLeftToClaim: z.number(),
  discountPercent: z.number(),
  planKey: paidPricingTableKeySchema,
});
export type CouponOfferStatus = z.infer<typeof couponOfferStatusSchema>;
const getCouponsResponseSchema = z.object({
  offers: z.array(couponOfferStatusSchema).default([]),
});
export type GetCouponsResponse = z.infer<typeof getCouponsResponseSchema>;

type AcceptPlanOfferRequest = {
  planKey: keyof PricingTable;
  redirectUrl: string;
  endTrialNow?: boolean;
  confirmTrialPeriodDays?: number;
  confirmCurrentPayment?: number;
  confirmOngoingPayment?: number;
  couponCode?: string;
  fbp?: string; // facebook ad tracking
  fbc?: string; // facebook click id
  gclid?: string; // google click id
  setupIntentId: string | undefined;
  rewardfulReferralId?: string;
};
const paymentAmmount = z.object({
  cents: z.number(),
  currency: z.string(),
  totalDiscount: z.number(),
});
const paymentInfoCommon = z.object({
  trialPeriodDays: z.number(),
  currentPayment: paymentAmmount,
  currentPaymentDateHuman: z.string(),
  ongoingPayment: paymentAmmount,
  ongoingInterval: z.enum(['month', 'year']),
  ongoingPaymentDateHuman: z.string(),
  coupon: z
    .object({
      name: z.string(),
      explanation: z.string(),
      variesWithTime: z.boolean(),
      metadata: z
        .object({
          title: z.string().nullish(),
          description: z.string().nullish(),
        })
        .nullish(),
    })
    .nullable(),
  couponError: z
    .string()
    .nullish()
    .transform((x) => x ?? undefined),
});
export type PaymentInfoCommon = z.infer<typeof paymentInfoCommon>;

const acceptPlanOfferResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('success') }),
  z.object({ status: z.literal('alreadyOnPlan') }),
  z
    .object({
      status: z.literal('requiresPaymentMethod'),
      stripePublicKey: z.string(),
      clientSecret: z.string(),
      confirmationFailed: z.boolean(),
      setupIntentError: z.string().nullable(),
    })
    .merge(paymentInfoCommon),
  z
    .object({
      status: z.literal('requiresPaymentConfirmation'),
      confirmationFailed: z.boolean(),
      paymentMethod: z.object({
        brand: z.string(),
        last4: z.string(),
      }),
    })
    .merge(paymentInfoCommon),
  z.object({
    status: z.literal('requiresPaymentIntentAction'),
    stripePublicKey: z.string(),
    clientSecret: z.string(),
  }),
  z.object({
    status: z.literal('generalPaymentError'),
    invoiceUrl: z.string(),
  }),
]);
export type AcceptPlanOfferResponse = z.infer<
  typeof acceptPlanOfferResponseSchema
>;

export type SaveChurnFeedbackRequest = {
  reason: string;
  feedback: string;
};

const getPausePlanOfferResponseSchema = z.object({
  offerPause: z.boolean(),
  currentPeriodEndHuman: z.string(),
  resumeDateHuman: z.string(),
});
export type GetPausePlanOfferResponse = z.infer<
  typeof getPausePlanOfferResponseSchema
>;

type AcceptPausePlanOfferRequest = {
  confirmResumeDateHuman: string;
};

const getCancelCouponOfferResponseSchema = z.object({
  discountOfferPercent: z.number().nullable(),
  mtyOffer: couponOfferStatusSchema.nullable(),
  userFirstName: z.string(),
  currentPeriodEndHuman: z.string(),
});
export type GetCancelCouponOfferResponse = z.infer<
  typeof getCancelCouponOfferResponseSchema
>;

const cancelPlanNowResponseSchema = z.object({
  currentPeriodEndHuman: z.string(),
});
export type CancelPlanNowResponse = z.infer<typeof cancelPlanNowResponseSchema>;

const reactivateCancellingPlanResponseSchema = z.object({
  success: z.literal(true),
});
export type ReactiveCancellingPlanResponse = z.infer<
  typeof reactivateCancellingPlanResponseSchema
>;

type CreateStripeBillingSessionRequest = {
  preferInvoice?: boolean;
  rewardfulReferralId?: string;
};
const createStripeBillingSessionResponseSchema = z.object({
  url: z.string(),
});
export type CreateStripeBillingSessionResponse = z.infer<
  typeof createStripeBillingSessionResponseSchema
>;

const getReferralLinkSchema = z.object({
  url: z.string(),
});
export type GetReferralLinkResponse = z.infer<typeof getReferralLinkSchema>;

const getTeamResponseSchema = z.object({
  name: z.string(),
  color: z.string().regex(/^#[0-9a-f]{6}$/),
  avatar: imageAssetSchema.nullish().transform((x) => x ?? undefined),
  logo: imageAssetSchema.nullish().transform((x) => x ?? undefined),
  needsToUpgrade: z.boolean(),
  members: z.array(
    z.object({
      id: z.number(),
      isMe: z.boolean(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      color: z.string().regex(/^#[0-9a-f]{6}$/),
      avatar: imageAssetSchema.nullable().transform((x) => x ?? undefined),
      teamRole: teamRoleSchema,
    }),
  ),
  invites: z.array(
    z.object({
      email: z.string(),
      role: teamRoleSchema,
      // what data type?
      // createdAt: z.string(),
    }),
  ),
  vacantSeats: z.number(),
});
export type GetTeamResponse = z.infer<typeof getTeamResponseSchema>;

type TeamUpdateRequest = {
  name?: string;
} & (
  | {
      role?: undefined;
      user?: undefined;
    }
  | {
      role: 'admin' | 'member';
      user: number;
    }
);
const teamUpdateResponseSchema = z.object({
  success: z.literal(true),
});
export type TeamUpdateResponse = z.infer<typeof teamUpdateResponseSchema>;

type TeamInviteRequest = {
  emails: readonly string[];
  role: TeamRole;
};
const teamInviteResponseSchema = z.object({
  emails: z.array(z.string()),
});
export type TeamInviteResponse = z.infer<typeof teamInviteResponseSchema>;

type TeamChangeInviteRequest = {
  email: string;
  role: TeamRole;
};
const teamChangeInviteResponseSchema = z.object({
  success: z.literal(true),
});
export type TeamChangeInviteResponse = z.infer<
  typeof teamChangeInviteResponseSchema
>;

type TeamDeleteInviteRequest = {
  email: string;
};
const teamDeleteInviteResponseSchema = z.object({
  success: z.literal(true),
});
export type TeamDeleteInviteResponse = z.infer<
  typeof teamDeleteInviteResponseSchema
>;

type TeamRemoveMemberRequest = {
  member: number;
};
const teamRemoveMemberResponseSchema = z.object({
  success: z.literal(true),
});
export type TeamRemoveMemberResponse = z.infer<
  typeof teamRemoveMemberResponseSchema
>;

type TeamAcceptInviteRequest = {
  token: string;
  dryRun: boolean;
};
const teamAcceptInviteResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('success'),
    invitedBy: z.string(),
    newTeamName: z.string(),
    type: z.enum(['invite', 'link']),
    oldTeam: z.object({
      name: z.string(),
      movedProjects: z.boolean(),
    }),
  }),
  z.object({
    status: z.literal('wrongEmail'),
    yourEmail: z.string(),
  }),
]);
export type TeamAcceptInviteResponse = z.infer<
  typeof teamAcceptInviteResponseSchema
>;
const teamGetInvitationLinkInfoSchema = z.object({
  enabled: z.boolean(),
  invitationLink: z
    .string()
    .optional()
    .transform((x) => x ?? null),
});
export type TeamGetInvitationLinkInfoResponse = z.infer<
  typeof teamGetInvitationLinkInfoSchema
>;
export type TeamGetOrCreateInvitationLinkResponse = z.infer<
  typeof teamGetInvitationLinkInfoSchema
>;
export type TeamResetInvitationLinkResponse = z.infer<
  typeof teamGetInvitationLinkInfoSchema
>;
export type TeamDisableInvitationLinkResponse = z.infer<
  typeof teamGetInvitationLinkInfoSchema
>;

export type ExchangeGoogleAccessTokenRequest = {
  accessToken: string;
  referralCode?: string;
  nonce: string;
};
export const exchangeGoogleAccessTokenResponseSchema = z.object({
  accessToken: z.string(),
  newUser: z.boolean(),
});
export type ExchangeGoogleAccessTokenResponse = z.infer<
  typeof exchangeGoogleAccessTokenResponseSchema
>;

export class HttpAccountsClient implements AccountsService {
  constructor(private readonly http: HttpEngine) {}

  async me() {
    return this.http.request(
      'GET',
      '/accounts/me',
      undefined,
      meResponseSchema,
    );
  }

  async getAnnouncement(id: string | number) {
    return this.http.request(
      'GET',
      `/accounts/announcement/${id}`,
      undefined,
      announcementSchema,
    );
  }

  async getBalance() {
    return this.http.request(
      'GET',
      `/accounts/balance`,
      undefined,
      balanceResponseSchema,
    );
  }

  async getReferralRewards() {
    return this.http.request(
      'GET',
      `/accounts/referral_rewards`,
      undefined,
      referralRewardResponseSchema,
    );
  }

  async getReferralInfo(code: string) {
    const params = new URLSearchParams();
    params.append('code', code);
    return this.http.request(
      'GET',
      `/accounts/referral_info?${params}`,
      undefined,
      referralInfoSchema,
    );
  }

  async login(req: LoginRequest) {
    return this.http.request(
      'POST',
      '/accounts/login',
      req,
      loginResponseSchema,
    );
  }

  async signup(req: SignupRequest) {
    return this.http.request(
      'POST',
      '/accounts/signup',
      req,
      signupResponseSchema,
    );
  }

  async verifyEmail(req: VerifyEmailRequest) {
    return this.http.request(
      'POST',
      '/accounts/verify_email',
      req,
      verifyEmailResponseSchema,
    );
  }

  async requestVerificationCode(req: RequestVerificationCodeRequest) {
    return this.http.request(
      'POST',
      '/accounts/request_verification_code',
      req,
      requestVerificationCodeResponseSchema,
    );
  }

  async resetPassword(req: ResetPasswordRequest) {
    return this.http.request(
      'POST',
      '/accounts/reset_password',
      req,
      resetPasswordResponseSchema,
    );
  }

  async changeEmailStart(req: ChangeEmailStartRequest) {
    return this.http.request(
      'POST',
      '/accounts/change_email/start',
      req,
      z.object({}),
    );
  }

  async changeEmailFinish(req: ChangeEmailFinishRequest) {
    return this.http.request(
      'POST',
      '/accounts/change_email/finish',
      req,
      z.object({}),
    );
  }

  async getChurnkeyHash() {
    return this.http.request(
      'GET',
      '/accounts/churn_hash',
      undefined,
      getChurnkeyHashResponseSchema,
    );
  }

  async linkOAuth(req: LinkOauthRequest) {
    return this.http.request(
      'POST',
      '/v1/oauth_write',
      req,
      linkOauthResponseSchema,
    );
  }

  async updateAttributes(req: UpdateAttributesRequest) {
    return this.http.request(
      'PUT',
      '/accounts/update_attributes',
      req,
      updateAttributesResponseSchema,
    );
  }

  async uploadAvatar(file: File) {
    const data = new FormData();
    data.append('0', file);
    return this.http.requestRaw(
      'PUT',
      'accounts/avatar',
      data,
      [],
      uploadAssetResponseSchema,
    );
  }

  async deleteAvatar() {
    return this.http.request(
      'DELETE',
      '/accounts/avatar',
      undefined,
      z.object({}),
    );
  }

  async uploadTeamAvatar(file: File) {
    const data = new FormData();
    data.append('0', file);
    return this.http.requestRaw(
      'PUT',
      'accounts/team_avatar',
      data,
      [],
      uploadAssetResponseSchema,
    );
  }

  async deleteTeamAvatar() {
    return this.http.request(
      'DELETE',
      '/accounts/team_avatar',
      undefined,
      z.object({}),
    );
  }

  async uploadTeamLogo(file: File) {
    const data = new FormData();
    data.append('0', file);
    return this.http.requestRaw(
      'PUT',
      'accounts/team_logo',
      data,
      [],
      uploadAssetResponseSchema,
    );
  }

  async deleteTeamLogo() {
    return this.http.request(
      'DELETE',
      '/accounts/team_logo',
      undefined,
      z.object({}),
    );
  }

  async getPricingTable(req?: GetPricingTableRequest) {
    const params = new URLSearchParams();
    if (req?.includeNextPayment) {
      params.append('includeNextPayment', 'true');
    }
    return this.http.request(
      'GET',
      `/accounts/pricing_table?${params}`,
      undefined,
      getPricingTableResponseSchema,
    );
  }

  async getCoupons() {
    return this.http.request(
      'GET',
      '/accounts/coupons',
      undefined,
      getCouponsResponseSchema,
    );
  }

  async acceptPlanOffer(req: AcceptPlanOfferRequest) {
    return this.http.request(
      'POST',
      '/accounts/accept_plan_offer',
      req,
      acceptPlanOfferResponseSchema,
    );
  }

  async saveChurnFeedback(req: SaveChurnFeedbackRequest) {
    return this.http.request(
      'POST',
      '/accounts/save_churn_feedback',
      req,
      z.object({}),
    );
  }

  async getPausePlanOffer() {
    return this.http.request(
      'GET',
      '/accounts/pause_plan_offer',
      undefined,
      getPausePlanOfferResponseSchema,
    );
  }

  async acceptPausePlanOffer(req: AcceptPausePlanOfferRequest) {
    return this.http.request(
      'POST',
      '/accounts/pause_plan_offer',
      req,
      z.object({}),
    );
  }

  async resumePlanNow() {
    return this.http.request(
      'POST',
      '/accounts/resume_plan_now',
      undefined,
      z.object({}),
    );
  }

  async getCancelCouponOffer() {
    return this.http.request(
      'GET',
      '/accounts/cancel/coupon_offer',
      undefined,
      getCancelCouponOfferResponseSchema,
    );
  }

  async acceptCancelCouponOffer() {
    return this.http.request(
      'POST',
      '/accounts/cancel/coupon_offer',
      undefined,
      z.object({}),
    );
  }

  async cancelPlanNow() {
    return this.http.request(
      'POST',
      '/accounts/cancel/now',
      undefined,
      cancelPlanNowResponseSchema,
    );
  }

  async reactivateCancellingPlan() {
    return this.http.request(
      'POST',
      '/accounts/reactivate_cancelling_plan',
      undefined,
      reactivateCancellingPlanResponseSchema,
    );
  }

  async createStripeBillingSession(req: CreateStripeBillingSessionRequest) {
    return this.http.request(
      'POST',
      '/accounts/create_stripe_billing_session',
      req,
      createStripeBillingSessionResponseSchema,
    );
  }

  async getTeam() {
    return this.http.request(
      'GET',
      '/accounts/team',
      undefined,
      getTeamResponseSchema,
    );
  }

  async teamUpdate(req: TeamUpdateRequest) {
    return this.http.request(
      'POST',
      '/accounts/team_update',
      req,
      teamUpdateResponseSchema,
    );
  }

  async teamInvite(req: TeamInviteRequest) {
    return this.http.request(
      'POST',
      '/accounts/team_invite',
      req,
      teamInviteResponseSchema,
    );
  }

  async teamChangeInvite(req: TeamChangeInviteRequest) {
    return this.http.request(
      'POST',
      '/accounts/team_change_invite',
      req,
      teamChangeInviteResponseSchema,
    );
  }

  async teamDeleteInvite(req: TeamDeleteInviteRequest) {
    return this.http.request(
      'POST',
      '/accounts/team_delete_invite',
      req,
      teamDeleteInviteResponseSchema,
    );
  }

  async teamRemoveMember(req: TeamRemoveMemberRequest) {
    return this.http.request(
      'POST',
      '/accounts/team_remove_member',
      req,
      teamRemoveMemberResponseSchema,
    );
  }

  async teamAcceptInvite(req: TeamAcceptInviteRequest) {
    return this.http.request(
      'POST',
      '/accounts/team_accept_invite',
      req,
      teamAcceptInviteResponseSchema,
    );
  }

  async getGrowthNudges() {
    return this.http.request(
      'GET',
      '/accounts/growth_nudge',
      undefined,
      growthNudgesResponseSchema,
    );
  }

  async updateGrowthNudge(
    key: keyof GrowthNudgesResponse,
    req: UpdateGrowthNudgeRequest,
  ) {
    return this.http.request('PUT', `/accounts/growth_nudge/${key}`, req);
  }

  async getPreferences() {
    return this.http.request(
      'GET',
      '/accounts/preferences',
      undefined,
      preferencesSchema,
    );
  }

  async updatePreference(
    key: keyof Preferences,
    req: UpdatePreferencesRequest,
  ) {
    return this.http.request('PUT', `/accounts/preferences/${key}`, req);
  }

  async getFeatureFlags() {
    return this.http.request(
      'GET',
      '/accounts/feature_flags',
      undefined,
      featureFlagsSchema,
    );
  }

  async joinSgbWaitlist() {
    return this.http.request(
      'POST',
      '/accounts/join_sgb_waitlist',
      undefined,
      z.object({}),
    );
  }

  getReferralLink(): Promise<GetReferralLinkResponse> {
    return this.http.request(
      'GET',
      '/accounts/get_referral_link',
      undefined,
      getReferralLinkSchema,
    );
  }

  async exchangeGoogleAccessTokenForToken(
    req: ExchangeGoogleAccessTokenRequest,
  ) {
    return this.http.request(
      'POST',
      '/accounts/google_token_exchange',
      req,
      exchangeGoogleAccessTokenResponseSchema,
    );
  }

  async teamGetInvitationLinkInfo() {
    return this.http.request(
      'GET',
      '/accounts/team_get_invitation_link_info',
      undefined,
      teamGetInvitationLinkInfoSchema,
    );
  }

  async teamGetOrCreateInvitationLink() {
    return this.http.request(
      'GET',
      '/accounts/team_get_or_create_invitation_link',
      undefined,
      teamGetInvitationLinkInfoSchema,
    );
  }

  async teamResetInvitationLink() {
    return this.http.request(
      'POST',
      '/accounts/team_reset_invitation_link',
      undefined,
      teamGetInvitationLinkInfoSchema,
    );
  }

  async teamDisableInvitationLink() {
    return this.http.request(
      'POST',
      '/accounts/team_disable_invitation_link',
      undefined,
      teamGetInvitationLinkInfoSchema,
    );
  }
}
